import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { cn } from '../lib/utils';
import { Typography } from '../Typography/Typography';

import { ALERT_INFO_SURFACE, ALERT_INFO_TEXT } from './AlertDialog';

interface AlertDialogPillProps {
  className?: string;
  description?: string;
  icon?: IconDefinition;
  onDismiss?: () => void;
  onMaximize: () => void;
  /** Marks the title as PHI (`mp-sensitive`): kept out of Mixpanel and blurred by "Hide PHI". */
  sensitive?: boolean;
  title: string;
}

/**
 * A minimized AlertDialog: the alert is still outstanding, just out of the way.
 * Clicking it puts the dialog back.
 *
 * Positioning is the caller's, so several pills can share one fixed container
 * rather than stacking on top of each other.
 */
function AlertDialogPill({
  className,
  description,
  icon,
  onDismiss,
  onMaximize,
  sensitive = false,
  title
}: AlertDialogPillProps) {
  return (
    <div
      className={cn(
        'relative flex w-full items-center gap-3 rounded-xl px-5 py-3 shadow-lg sm:w-auto sm:rounded-full',
        // The same surface the dialog uses, so minimizing changes the size of the
        // alert and nothing else about it.
        ALERT_INFO_SURFACE,
        // The alert is still outstanding, so the pill keeps asking to be noticed.
        // border-blue-500 is one of the few stock families dark-mode.css maps to
        // itself, so it holds in both themes.
        'animate-pulse border-4 border-blue-500',
        className
      )}>
      {/*
        Covers the pill rather than wrapping its content, so the padding and the
        gap between the two controls maximize as readily as the text does. Being
        positioned puts it over the content; the dismiss button sits over it in
        turn.
      */}
      <button
        type="button"
        onClick={onMaximize}
        aria-label={`Show alert: ${title}`}
        className={cn('absolute inset-0 rounded-xl sm:rounded-full', sensitive && 'mp-sensitive')}
      />

      {icon && <FontAwesomeIcon icon={icon} className="text-sm" aria-hidden />}

      {/*
        The text never takes clicks: the maximize overlay underneath does. Hide PHI's blur
        filter gives a sensitive title its own stacking context above that overlay, which
        would otherwise swallow the click.
      */}
      <span className="pointer-events-none flex flex-1 flex-col leading-tight">
        {description && (
          <Typography as="span" variant="caption" className={ALERT_INFO_TEXT}>
            {description}
          </Typography>
        )}
        {/* Typography sets a near-black of its own, so the colour must be stated. */}
        <Typography
          as="span"
          variant="body"
          weight="medium"
          className={cn(ALERT_INFO_TEXT, sensitive && 'mp-sensitive')}>
          {title}
        </Typography>
      </span>

      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={`Dismiss alert: ${title}`}
          className={cn(
            'relative shrink-0 rounded p-1 transition-colors',
            ALERT_INFO_TEXT,
            'hover:bg-blue-100 dark:hover:bg-blue-900'
          )}>
          <FontAwesomeIcon icon={faTimes} className="text-sm" />
        </button>
      )}
    </div>
  );
}

export { AlertDialogPill };
export type { AlertDialogPillProps };
